import { useContext, useMemo } from 'react';
import UserContext from '../context/user';

export type SetupRequirementKey = 'company' | 'site';

export type SetupRequirement = {
  key: SetupRequirementKey;
  title: string;
  // What is actually outstanding, e.g. ['company name', 'company address'].
  missingLabels: string[];
  // Why it's needed, in the user's terms.
  why: string;
  // Button label for the person who can action it.
  actionLabel: string;
  settingsPath: string;
  settingsLabel: string;
  // These are team settings only an owner can change, so teammates are told
  // who to ask rather than sent to a page the API would reject them from.
  ownerOnly: boolean;
};

// "a", "a and b", "a, b and c"
export const listJoin = (items: string[]) =>
  items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

/**
 * What the account still needs before it can add or reserve containers.
 *
 * The API rejects containers without a company name or a site, and the
 * company address positions the company on the map, so checking up front lets
 * the UI say so before someone fills in a form that can't succeed. Grouped by
 * where each is fixed, so one trip to a settings page is one prompt.
 *
 * Single source of truth for the setup banners and the Add Container guard.
 */
export default function useAccountSetup() {
  const { user, clients, isLoggedIn } = useContext(UserContext);

  return useMemo(() => {
    const isAdmin = !!user?.roles?.includes('ADMIN');
    const isTeammate = !!user?.isTeammate;
    const missing: SetupRequirement[] = [];

    // Admins act on behalf of the platform, not a company of their own.
    if (isLoggedIn && !isAdmin) {
      // Company name and address are both edited on the same page, so they
      // travel together as one prompt.
      const companyGaps: string[] = [];
      if (!user?.companyInformation?.companyName) companyGaps.push('company name');
      if (!user?.companyInformation?.address) companyGaps.push('company address');
      if (companyGaps.length > 0) {
        const reasons = [];
        if (!user?.companyInformation?.companyName) {
          reasons.push('Re-use requests are sent to shipping lines on behalf of your company');
        }
        if (!user?.companyInformation?.address) {
          // The address (and the coordinates saved with it) seeds the company's
          // own Fineprint site; without it that site sits at 0,0.
          reasons.push(
            `${
              reasons.length ? 'and its address' : 'Your company address'
            } locates you on the map so distances and savings are correct`
          );
        }
        missing.push({
          key: 'company',
          title: 'Your company details are incomplete',
          missingLabels: companyGaps,
          why: `${reasons.join(', ')}.`,
          actionLabel: 'Add Company Details',
          settingsPath: '/settings/edit-account',
          settingsLabel: 'Settings > Edit Account',
          ownerOnly: true,
        });
      }
      if (clients.length === 0) {
        missing.push({
          key: 'site',
          title: 'Your team has no site set up',
          // Nothing to enumerate — the reason below already names it.
          missingLabels: [],
          why: 'Containers are stacked at a site, so at least one is needed before they can be added or reserved.',
          actionLabel: 'Set Up Site',
          settingsPath: '/settings/our-sites',
          settingsLabel: 'Settings > Our Sites',
          // Creating a client is owner-only on the API (clientRoutes), so a
          // teammate sent there would just hit Unauthorized.
          ownerOnly: true,
        });
      }
    }

    return {
      missing,
      isReady: missing.length === 0,
      isTeammate,
    };
  }, [clients.length, isLoggedIn, user]);
}
